import * as polygonClipping from "polygon-clipping";
import * as THREE from "three";
import { ClippingPolygonType } from "../../types/floorplannerTypes";

type Pair = [number, number];
type Ring = Pair[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

const EPSILON = 1e-6;
const MIN_AREA = 1e-10;

const distanceSq = (a: Pair, b: Pair): number => {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
};

const isSamePoint = (a: Pair, b: Pair, epsilon = EPSILON): boolean =>
  distanceSq(a, b) <= epsilon * epsilon;

const signedRingArea = (ring: Ring): number => {
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const next = (i + 1) % ring.length;
    area += ring[i][0] * ring[next][1] - ring[next][0] * ring[i][1];
  }
  return area / 2;
};

const warnOnce = (key: string, message: string, data?: unknown): void => {
  if (warnedWallClippingGeometry.has(key)) return;
  warnedWallClippingGeometry.add(key);
  console.warn(message, data);
};

const warnedWallClippingGeometry = new Set<string>();

const normalizeRing = (points: THREE.Vector2[] | Pair[]): Ring | null => {
  const ring: Ring = [];
  let hasInvalidCoordinate = false;

  points.forEach((point) => {
    const pair: Pair = Array.isArray(point)
      ? [point[0], point[1]]
      : [point.x, point.y];

    if (!Number.isFinite(pair[0]) || !Number.isFinite(pair[1])) {
      hasInvalidCoordinate = true;
      return;
    }

    if (ring.length === 0 || !isSamePoint(ring[ring.length - 1], pair)) {
      ring.push(pair);
    }
  });

  if (ring.length > 1 && isSamePoint(ring[0], ring[ring.length - 1])) {
    ring.pop();
  }

  if (hasInvalidCoordinate || ring.length < 3 || Math.abs(signedRingArea(ring)) <= MIN_AREA) {
    return null;
  }

  return [...ring, [...ring[0]]];
};

const shapeToPolygon = (shape: THREE.Shape): Polygon | null => {
  const outerRing = normalizeRing(shape.getPoints());
  if (!outerRing) return null;

  const holes = shape.holes
    .map((hole) => normalizeRing(hole.getPoints()))
    .filter((hole): hole is Ring => hole !== null);

  return [outerRing, ...holes];
};

const clippingToPolygon = (clipping: ClippingPolygonType): Polygon | null => {
  const ring = normalizeRing(clipping.polygon);
  return ring ? [ring] : null;
};

const ringToVector2 = (ring: Ring): THREE.Vector2[] => {
  const openRing = ring.length > 1 && isSamePoint(ring[0], ring[ring.length - 1])
    ? ring.slice(0, -1)
    : ring;

  return openRing.map(([x, y]) => new THREE.Vector2(x, y));
};

const multiPolygonToShapes = (multiPolygon: MultiPolygon): THREE.Shape[] => {
  const nextShapes: THREE.Shape[] = [];

  multiPolygon.forEach((polygon) => {
    if (polygon.length === 0) return;

    const outerRing = normalizeRing(polygon[0]);
    if (!outerRing) return;

    const shape = new THREE.Shape(ringToVector2(outerRing));

    for (let i = 1; i < polygon.length; i++) {
      const holeRing = normalizeRing(polygon[i]);
      if (holeRing) {
        shape.holes.push(new THREE.Path().setFromPoints(ringToVector2(holeRing)));
      }
    }

    nextShapes.push(shape);
  });

  return nextShapes;
};

/**
 * Applies wall attachment cutouts defensively. Each clipping polygon is subtracted
 * independently so one invalid or numerically fragile cutout cannot make every
 * opening in the wall disappear.
 */
export const applyClippingPolygonsToShapes = (
  shapes: THREE.Shape[],
  clippingPolygons: ClippingPolygonType[],
  wallId?: string,
): void => {
  if (!shapes.length || !clippingPolygons.length) return;

  let subject = shapes
    .map(shapeToPolygon)
    .filter((polygon): polygon is Polygon => polygon !== null);

  if (subject.length === 0) {
    warnOnce(
      `invalid-subject:${wallId ?? "unknown"}`,
      `[wallClippingGeometry] Skipping wall clipping because wall fill geometry is invalid`,
      { wallId },
    );
    return;
  }

  let attemptedClip = false;

  clippingPolygons.forEach((clipping) => {
    const clippingPolygon = clippingToPolygon(clipping);
    if (!clippingPolygon) {
      warnOnce(
        `invalid-clip:${wallId ?? "unknown"}:${clipping.belongsTo}`,
        `[wallClippingGeometry] Skipping invalid wall clipping polygon`,
        { wallId, clipping },
      );
      return;
    }

    try {
      const nextSubject = polygonClipping.difference(subject, clippingPolygon) as MultiPolygon;
      if (Array.isArray(nextSubject)) {
        subject = nextSubject;
        attemptedClip = true;
      }
    } catch (error) {
      warnOnce(
        `failed-clip:${wallId ?? "unknown"}:${clipping.belongsTo}`,
        `[wallClippingGeometry] Skipping wall clipping polygon after boolean difference failed`,
        { wallId, clipping, error },
      );
    }
  });

  if (!attemptedClip) return;

  if (subject.length === 0) {
    shapes.length = 0;
    return;
  }

  const nextShapes = multiPolygonToShapes(subject);
  if (nextShapes.length === 0) {
    warnOnce(
      `invalid-result:${wallId ?? "unknown"}`,
      `[wallClippingGeometry] Keeping original wall fill because clipping produced invalid geometry`,
      { wallId },
    );
    return;
  }

  shapes.splice(0, shapes.length, ...nextShapes);
};
